// window.__ENV__ генерится entrypoint-скриптом в env.js при старте контейнера.
declare global { interface Window { __ENV__?: { API_BASE?: string } } }
const BASE: string = (window.__ENV__ && window.__ENV__.API_BASE)
  || "http://localhost:8080";

const TOKEN_KEY = "altuwka_session";
const GIFTASSET_ID_KEY = "altuwka_giftasset_id";
const CHAT_ID_KEY = "altuwka_chat_id";
const CLIENT_ID = "web";
const AUTH_EXPIRED_EVENT = "altuwka:auth-expired";

let sessionCleanup: Promise<void> | null = null;

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getGiftassetId(): string {
  const current = localStorage.getItem(GIFTASSET_ID_KEY);
  if (current && current.length === 32) return current;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  localStorage.setItem(GIFTASSET_ID_KEY, value);
  return value;
}

export function getChatId(): string {
  const current = localStorage.getItem(CHAT_ID_KEY);
  if (current) return current;
  const value = crypto.randomUUID();
  localStorage.setItem(CHAT_ID_KEY, value);
  return value;
}

export interface ApiError extends Error {
  code?: string;
  status?: number;
  meta?: Record<string, unknown>;
}

function makeError(response: Response, data: any): ApiError {
  const err: ApiError = new Error(
    data?.message || data?.error || `HTTP ${response.status}`
  );
  err.code = data?.error_code || data?.code;
  err.status = response.status;
  err.meta = data;
  return err;
}

async function clearExpiredSession(staleToken: string | null): Promise<void> {
  if (sessionCleanup) return sessionCleanup;
  setToken(null);
  sessionCleanup = (async () => {
    try {
      await fetch(BASE + "/api/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(staleToken ? { Authorization: "Bearer " + staleToken } : {}),
        },
        credentials: "include",
      });
    } catch {
      // localStorage is already clean; a failed cookie cleanup is retried on next login.
    } finally {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      sessionCleanup = null;
    }
  })();
  return sessionCleanup;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Client-ID": CLIENT_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  let data: any = null;
  try { data = await response.json(); } catch {}
  if (response.status === 401) await clearExpiredSession(token);
  if (!response.ok) throw makeError(response, data);
  return data as T;
}

async function chatCall<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${BASE}/app/api/chat/v1${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-GIFTASSET-ID": getGiftassetId(),
      "X-Client-ID": CLIENT_ID,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data: any = null;
  try { data = await response.json(); } catch {}
  if (!response.ok) throw makeError(response, data);
  return data as T;
}

export const api = {
  // ---- auth / billing session ----
  authInit: () => call<{ state: string; login_url: string }>("POST", "/api/auth/init"),
  authCheck: (state: string) =>
    call<{ status: string; session_token?: string; user_id?: number; is_admin?: boolean }>(
      "GET", `/api/auth/check?state=${encodeURIComponent(state)}`
    ),
  logout: () => call("POST", "/api/auth/logout"),

  // ---- shared chat v1 (web/mobile/API) ----
  chatAccess: () => chatCall<{ status: string }>("GET", "/access"),
  chatConfig: () => chatCall<{
    api_version: string;
    streaming: boolean;
    message_count: number;
    telegram_authorized: boolean;
    subscription_active: boolean;
    demo_messages_left: number;
    bot_auth_link: string;
    buy_link: string;
  }>("GET", "/config"),
  chatHistory: (chatId = getChatId()) =>
    chatCall<{ messages: any[] }>(
      "GET", `/chats/${encodeURIComponent(chatId)}/messages?limit=100`
    ),
  clearChatHistory: (chatId = getChatId()) =>
    chatCall("DELETE", `/chats/${encodeURIComponent(chatId)}/messages`),
  chatEvents: (chatId = getChatId(), timeout = 25) =>
    chatCall<{ messages: any[]; timeout: boolean }>(
      "GET",
      `/chats/${encodeURIComponent(chatId)}/events?timeout=${timeout}`,
    ),
  answerChoice: (questionId: string, selected: number[]) =>
    chatCall("POST", "/ask-choice/answer", {
      question_id: questionId,
      selected,
    }),

  async streamChatMessage(
    text: string,
    chatId = getChatId(),
    onEvent: (event: any) => void,
  ): Promise<void> {
    const response = await fetch(`${BASE}/app/api/chat/v1/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
        "X-GIFTASSET-ID": getGiftassetId(),
        "X-Client-ID": CLIENT_ID,
      },
      body: JSON.stringify({ text, chat_id: chatId, stream: true }),
    });
    if (!response.ok || !response.body) {
      let data: any = null;
      try { data = await response.json(); } catch {}
      throw makeError(response, data);
    }
    await readSse(response.body, onEvent);
  },

  // ---- credits / payments ----
  credits: () => call<any>("GET", "/api/credits/"),
  buyInfo: () => call<any>("GET", "/api/credits/buy"),
  payments: () => call<any>("GET", "/api/payments/"),

  // ---- subscription ----
  subscription: () => call<{
    ton_wallet: string;
    current: { active: boolean; tier_id?: string; tier_name?: string; expires_at?: string };
    tiers: { id: string; name: string; price_ton: number; credits: number; days: number; memo: string }[];
    instructions: string;
  }>("GET", "/api/subscription/"),

  // ---- MCP API access ----
  mcpKey: () => call<any>("GET", "/api/mcp/key"),
  rotateMcpKey: () => call<any>("POST", "/api/mcp/key", { name: "default" }),
  revokeMcpKey: () => call<any>("DELETE", "/api/mcp/key"),
};

async function readSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: any) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    let boundary: number;
    while ((boundary = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) continue;
        const raw = line.slice(5).trim();
        if (!raw || raw === "[DONE]") continue;
        try { onEvent(JSON.parse(raw)); } catch {}
      }
    }
    if (done) break;
  }
}
